import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export const getSummary = async (businessId: string) =>
  httpRequest.get(`/inv/ar/${businessId}/summary`);

export const getStatement = async (businessId: string, params: {
  periodFrom: string;
  periodTo: string;
  page?: number;
  limit?: number;
}) => httpRequest.get(`/inv/ar/${businessId}/statement`, params);

/** Field names the reconciliation report can disagree on. */
export type VerifiedField =
  | "currentBalance"
  | "overdueBalance"
  | "bucketCurrent"
  | "bucket1to30"
  | "bucket31to60"
  | "bucket61to90"
  | "bucket91Plus";

export interface IVerifyComparison {
  field: VerifiedField;
  /** What the invoice-scanning reference computes. */
  reference: number;
  /** What the AR ledger snapshot holds. */
  ledger: number;
  diff: number;
}

export interface IVerifyRow {
  sellerBusinessId: string;
  buyerBusinessId: string;
  sellerName: string;
  buyerName: string;
  comparisons: IVerifyComparison[];
}

/** One checked pair, matched or not. */
export interface IVerifyPair extends IVerifyRow {
  /** Balance computed from invoices. */
  reference: number;
  /** AR sub-ledger balance. */
  ledger: number;
  /** General ledger 1200.* balance of the buyer; null when unavailable. */
  gl: number | null;
  /** reference - ledger. */
  diff: number;
  mismatched: boolean;
}

export interface IVerifyReport {
  checkedAt: string;
  /** The signed-in business the report is scoped to. */
  businessId: string | null;
  checked: number;
  matched: number;
  mismatched: number;
  /** Only the pairs that disagree — a clean ledger returns an empty array. */
  rows: IVerifyRow[];
  /** Every checked pair of the signed-in business. */
  pairs: IVerifyPair[];
}

/** Reconciles the AR ledger against the invoice-scanning reference. Read-only. */
export const getVerifyReport = async (params?: { limit?: number }) =>
  httpRequest.get<IVerifyReport>("/inv/ar/verify", params ?? {});

export interface IGapPreviewRow {
  date: string;
  kind: string;
  transactionType: string;
  invoiceId: string;
  refCode: string;
  /** Always positive. */
  amount: number;
  /** Signed effect on the balance. */
  delta: number;
  description: string;
}

export interface IGapUnreconciled {
  invoiceId: string;
  refCode: string;
  reason: "OVERPAID" | "NET_MISMATCH";
  netAfter: number;
  expected: number;
  overpaid: { confirmed: number; payments: number[]; paid: number } | null;
}

export interface IGapPair {
  sellerBusinessId: string;
  buyerBusinessId: string;
  before: number;
  after: number;
  rows: IGapPreviewRow[];
  unreconciled: IGapUnreconciled[];
}

export interface IGapRunResult {
  dryRun: boolean;
  scanned: number;
  pairs: IGapPair[];
  written: { posted: number; invoices: number; failures: string[] } | null;
}

/**
 * Adds the AR rows an invoice is missing. `dryRun` (default) writes nothing and
 * returns the preview; `dryRun: false` writes. Only the signed-in business as seller.
 */
export const gapBackfill = async (body: { buyerBusinessId?: string; dryRun?: boolean }) =>
  httpRequest.post<IGapRunResult>("/inv/ar/gap-backfill", body);
